/**
 * Mascarade Noire — Le Chaudron des recommandations
 * Quiz autonome, accessible au clavier, qui pioche dans le vrai catalogue
 * (rendu côté serveur dans le <script type="application/json"> voisin).
 * N'appelle que l'API panier publique de Shopify (/cart/add.js) : aucune
 * dépendance sur les composants internes du thème.
 *
 * Première question : "Tu cherches..." — un costume (3 questions : style,
 * coupe, budget ; la boutique ne vend que des tailles adultes)
 * ou "C'est pour offrir" (va directement piocher jusqu'à 3 produits tagués
 * "Offrande", sans passer par le scoring style/budget).
 */
(function () {
  'use strict';

  var QUESTIONS = [
    {
      id: 'style',
      label: 'Quel style ?',
      options: [
        { value: 'terrifiant', label: 'Terrifiant' },
        { value: 'glamour', label: 'Glamour' },
        { value: 'mysterieux', label: 'Mystérieux' },
      ],
    },
    {
      id: 'coupe',
      label: 'Quelle coupe ?',
      options: [
        { value: 'homme', label: 'Homme' },
        { value: 'femme', label: 'Femme' },
        { value: 'peu-importe', label: 'Peu importe' },
      ],
    },
    {
      id: 'budget',
      label: 'Quel budget ?',
      options: [
        { value: 'eco', label: 'Moins de 30€', target: 25 },
        { value: 'standard', label: '30€ – 60€', target: 45 },
        { value: 'premium', label: 'Plus de 60€', target: 75 },
      ],
    },
  ];

  function normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function scoreProduct(product, answers) {
    var score = 0;
    var tags = (product.tags || []).map(normalize);

    if (answers.style && tags.indexOf(normalize(answers.style)) !== -1) score += 10;
    // Une coupe unisexe convient aux deux.
    if (
      (answers.coupe === 'homme' || answers.coupe === 'femme') &&
      (tags.indexOf(answers.coupe) !== -1 || tags.indexOf('unisexe') !== -1)
    ) {
      score += 10;
    }

    var budgetOption = QUESTIONS.find(function (q) {
      return q.id === 'budget';
    }).options.find(function (o) {
      return o.value === answers.budget;
    });
    if (budgetOption) {
      score -= Math.abs(product.price / 100 - budgetOption.target) / 5;
    }
    if (product.available) score += 1;
    return score;
  }

  function pickRecommendations(products, answers) {
    // Un produit en rupture de stock n'est jamais recommandé (sauf s'il ne
    // reste vraiment rien d'autre à proposer).
    // Parcours « costume » : uniquement les costumes et accessoires à porter
    // (jamais la décoration ou la papeterie).
    var wearable = products.filter(function (p) {
      return (p.tags || []).some(function (t) {
        var n = normalize(t);
        return n === 'costume complet' || n === 'accessoire';
      });
    });
    if (!wearable.length) wearable = products;
    var inStock = wearable.filter(function (p) {
      return p.available;
    });
    var pool = inStock.length ? inStock : wearable;
    var scored = pool
      .map(function (p) {
        return { product: p, score: scoreProduct(p, answers) };
      })
      .sort(function (a, b) {
        return b.score - a.score;
      });
    return scored.slice(0, 3).map(function (s) {
      return s.product;
    });
  }

  function pickGifts(products) {
    return products
      .filter(function (p) {
        return p.available && (p.tags || []).some(function (t) {
          return normalize(t) === normalize('Offrande');
        });
      })
      .slice(0, 3);
  }

  function formatPrice(cents) {
    return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  function initChaudron(root) {
    var dataScript = root
      .closest('.mn-chaudron')
      .querySelector('script[data-mn-chaudron-products]');
    var products = [];
    try {
      products = JSON.parse(dataScript.textContent || '[]');
    } catch (e) {
      products = [];
    }

    var statusEl = root.querySelector('[data-mn-chaudron-status]');
    var answers = {};
    var stepIndex = 0;
    var intentMode = null; // null = pas encore choisi, 'costume' ou 'cadeau'

    function buildProductCard(product, addLabel) {
      var card = document.createElement('div');
      card.style.cssText =
        'background: rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:10px; display:flex; flex-direction:column; gap:8px;';

      if (product.image) {
        var img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        img.style.cssText = 'width:100%; height:120px; object-fit:cover; border-radius:8px;';
        card.appendChild(img);
      }

      var title = document.createElement('a');
      title.href = product.url;
      title.textContent = product.title;
      title.style.cssText = 'font-family: var(--mn-font-body); font-size:14px; color: var(--mn-bone); text-decoration:none;';
      card.appendChild(title);

      var price = document.createElement('span');
      price.textContent = formatPrice(product.price);
      price.style.cssText = 'font-family: var(--mn-font-body); font-size:13px; opacity:0.8;';
      card.appendChild(price);

      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      var needsSize = product.variantsCount > 1;
      addBtn.textContent = !product.available
        ? 'Voir le produit'
        : needsSize
          ? 'Choisir ma taille'
          : addLabel || 'Ajouter au panier';
      addBtn.style.cssText =
        'min-height:44px; border-radius:999px; border:none; background: var(--mn-orange-hot); color: var(--mn-night); ' +
        'font-weight:700; font-family: var(--mn-font-body); cursor:pointer;';
      addBtn.addEventListener('click', function () {
        if (!product.available || !product.variantId || needsSize) {
          window.location.href = product.url;
          return;
        }
        addBtn.disabled = true;
        addBtn.textContent = 'Ajout…';
        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: product.variantId, quantity: 1 }),
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add-to-cart-failed');
            return res.json();
          })
          .then(function () {
            addBtn.textContent = '✓ Ajouté';
            document.dispatchEvent(new CustomEvent('cart:updated'));
          })
          .catch(function () {
            addBtn.disabled = false;
            addBtn.textContent = 'Réessayer';
          });
      });
      card.appendChild(addBtn);

      return card;
    }

    function render() {
      root.innerHTML = '';

      if (!products.length) {
        var p = document.createElement('p');
        p.textContent = 'Le chaudron est vide pour le moment — reviens bientôt.';
        root.appendChild(p);
        return;
      }

      if (intentMode === null) {
        renderIntent();
      } else if (intentMode === 'cadeau') {
        renderGiftResults();
      } else if (stepIndex < QUESTIONS.length) {
        renderQuestion(QUESTIONS[stepIndex]);
      } else {
        renderResults();
      }
    }

    function renderIntent() {
      var wrap = document.createElement('div');

      var h3 = document.createElement('h3');
      h3.style.cssText = 'font-family: var(--mn-font-title); font-size: 20px; margin: 0 0 16px;';
      h3.textContent = 'Tu cherches...';
      wrap.appendChild(h3);

      var optWrap = document.createElement('div');
      optWrap.style.cssText = 'display:flex; flex-wrap:wrap; gap:10px; justify-content:center;';

      var costumeBtn = document.createElement('button');
      costumeBtn.type = 'button';
      costumeBtn.textContent = 'Un costume pour moi / nous';
      costumeBtn.style.cssText =
        'min-height:44px; padding:10px 18px; border-radius:999px; border:1px solid var(--mn-orange); ' +
        'background: var(--mn-night); color: var(--mn-bone); font-family: var(--mn-font-body); cursor:pointer;';
      costumeBtn.addEventListener('click', function () {
        intentMode = 'costume';
        stepIndex = 0;
        answers = {};
        render();
      });
      optWrap.appendChild(costumeBtn);

      var giftBtn = document.createElement('button');
      giftBtn.type = 'button';
      giftBtn.textContent = "C'est pour offrir 🎁";
      giftBtn.style.cssText =
        'min-height:44px; padding:10px 18px; border-radius:999px; border:1px solid #f0c419; ' +
        'background: var(--mn-night); color: var(--mn-bone); font-family: var(--mn-font-body); cursor:pointer;';
      giftBtn.addEventListener('click', function () {
        intentMode = 'cadeau';
        render();
      });
      optWrap.appendChild(giftBtn);

      wrap.appendChild(optWrap);
      root.appendChild(wrap);
    }

    function renderQuestion(question) {
      var wrap = document.createElement('div');

      var progress = document.createElement('p');
      progress.setAttribute('aria-live', 'polite');
      progress.style.cssText = 'font-size:12px; opacity:0.7; margin:0 0 12px; font-family: var(--mn-font-body);';
      progress.textContent = 'Question ' + (stepIndex + 1) + ' / ' + QUESTIONS.length;
      wrap.appendChild(progress);

      var h3 = document.createElement('h3');
      h3.style.cssText = 'font-family: var(--mn-font-title); font-size: 20px; margin: 0 0 16px;';
      h3.textContent = question.label;
      wrap.appendChild(h3);

      var optWrap = document.createElement('div');
      optWrap.style.cssText = 'display:flex; flex-wrap:wrap; gap:10px; justify-content:center;';

      question.options.forEach(function (opt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = opt.label;
        btn.style.cssText =
          'min-height:44px; padding:10px 18px; border-radius:999px; border:1px solid var(--mn-orange); ' +
          'background: var(--mn-night); color: var(--mn-bone); font-family: var(--mn-font-body); cursor:pointer;';
        btn.addEventListener('click', function () {
          answers[question.id] = opt.value;
          stepIndex += 1;
          render();
        });
        optWrap.appendChild(btn);
      });

      wrap.appendChild(optWrap);

      var back = document.createElement('button');
      back.type = 'button';
      back.textContent = '← Retour';
      back.style.cssText =
        'margin-top:16px; background:none; border:none; color: var(--mn-bone); opacity:0.6; ' +
        'font-family: var(--mn-font-body); font-size:13px; cursor:pointer; min-height:44px;';
      back.addEventListener('click', function () {
        if (stepIndex > 0) {
          stepIndex -= 1;
        } else {
          intentMode = null;
        }
        render();
      });
      wrap.appendChild(back);

      root.appendChild(wrap);
    }

    function renderResults() {
      var recos = pickRecommendations(products, answers);

      var wrap = document.createElement('div');

      var h3 = document.createElement('h3');
      h3.style.cssText = 'font-family: var(--mn-font-title); font-size: 20px; margin: 0 0 16px;';
      h3.textContent = 'La potion révèle...';
      wrap.appendChild(h3);

      var grid = document.createElement('div');
      grid.style.cssText = 'display:grid; grid-template-columns: repeat(auto-fit, minmax(140px,1fr)); gap:14px; text-align:left;';

      recos.forEach(function (product) {
        grid.appendChild(buildProductCard(product));
      });

      wrap.appendChild(grid);

      var retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = '🔁 Relancer la potion';
      retry.style.cssText =
        'margin-top:16px; min-height:44px; padding:10px 18px; border-radius:999px; border:1px solid var(--mn-orange); ' +
        'background:none; color: var(--mn-bone); font-family: var(--mn-font-body); cursor:pointer;';
      retry.addEventListener('click', function () {
        answers = {};
        stepIndex = 0;
        intentMode = null;
        render();
      });
      wrap.appendChild(retry);

      root.appendChild(wrap);
    }

    function renderGiftResults() {
      var gifts = pickGifts(products);

      var wrap = document.createElement('div');

      var h3 = document.createElement('h3');
      h3.style.cssText = 'font-family: var(--mn-font-title); font-size: 20px; margin: 0 0 16px;';
      h3.textContent = gifts.length ? 'Voici de quoi offrir 🎁' : 'Aucune offrande disponible pour le moment';
      wrap.appendChild(h3);

      if (gifts.length) {
        var grid = document.createElement('div');
        grid.style.cssText = 'display:grid; grid-template-columns: repeat(auto-fit, minmax(140px,1fr)); gap:14px; text-align:left;';

        gifts.forEach(function (product) {
          grid.appendChild(buildProductCard(product, 'Offrir ce cadeau'));
        });

        wrap.appendChild(grid);
      }

      var back = document.createElement('button');
      back.type = 'button';
      back.textContent = '← Autre chemin';
      back.style.cssText =
        'margin-top:16px; background:none; border:none; color: var(--mn-bone); opacity:0.6; ' +
        'font-family: var(--mn-font-body); font-size:13px; cursor:pointer; min-height:44px;';
      back.addEventListener('click', function () {
        intentMode = null;
        render();
      });
      wrap.appendChild(back);

      root.appendChild(wrap);
    }

    render();
    if (statusEl) statusEl.remove();
  }

  function boot() {
    document.querySelectorAll('[data-mn-chaudron-quiz]').forEach(function (root) {
      if (root.dataset.mnChaudronInit === 'true') return;
      root.dataset.mnChaudronInit = 'true';
      initChaudron(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
})();
